import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import Button from '../../../../../elements/button'
import Icon from '../../../../../components/icons'
import { useParams, useSearchParams } from 'react-router-dom'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import { ErrorResponse } from '../../../../../store/api/apiTypes'
import Dropdown from '../../../../../elements/Form/dropdown'
import { useExperimentSelector } from '../../../../../store/selectors/experimentSelector'
import Label from '../../../../../elements/formLabel'
import { getSelectedOption, getSelectedOptions, trackEvent } from '../../../../../_common/functions'
import { DataTableTypes, experimentTypes } from '../../../../../_config/types'
import { useFpLoader } from '../../../../../store/api/hooks/fpLoaderHook'

interface Props {
    tables: any[]
    applyFilter: (data: any) => void
    _for: experimentTypes
    experimentLiteral?: string
}

const AddFilters: React.FC<Props> = ({ tables, applyFilter, _for, experimentLiteral }) => {
    const { t } = useTranslation()
    const params = useParams()
    const literal = experimentLiteral || params.experiment
    const experimentApi = useExperimentApi()
    const experiment: any = useExperimentSelector()
    const [searchParams] = useSearchParams()
    const [tableType, setTableType] = useState<DataTableTypes | null>(null)
    const [tableFilter, setTableFilter] = useState<any>(null)
    const { showLoader, hideLoader } = useFpLoader()

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<{
        filters: { column: any; value: any; columns?: any; id?: any }[]
    }>()

    const {
        fields: filters,
        append,
        update,
        remove,
    } = useFieldArray({
        control,
        name: 'filters',
    })
    const getColumnOptions = (resp: any) => {
        const options = resp.data.map((key: any) => ({
            value: key,
            label: key,
        }))
        return options
    }

    const getColumns = (index: number, data: any) => {
        return new Promise((resolve, reject) => {
            showLoader()
            const spFor = searchParams.get('for')
            let table_type = 'KPI'
            if (spFor) {
                table_type = spFor == 'intervention' ? 'Intervention' : 'Recommendation'
            }
            experimentApi.getColumnPreviwData(
                {
                    literal,
                    colname: data.value,
                    table_type,
                },
                (resp: any) => {
                    const options = getColumnOptions(resp)
                    update(index, {
                        ...filters[index],
                        column: data,
                        columns: options,
                    })
                    hideLoader()
                    resolve(options)
                },
                (error: ErrorResponse<[]>) => {
                    hideLoader()
                },
            )
        })
    }

    const sendFilter = (data: any) => {
        trackEvent('measurement_filter_applied', { experiment_literal: literal })
        const _filter: any = {}
        data.filters.map((filter: any) => {
            _filter[filter.column.value] = filter.value.map((v: any) => v.value)
        })
        applyFilter({
            filters: _filter,
        })
    }

    const filtersWatch = watch('filters')

    useEffect(() => {
        if (tableType) {
            remove()
            if (tableFilter?.filters && Object.keys(tableFilter?.filters).length > 0) {
                let i = 0
                for (const key of Object.keys(tableFilter?.filters)) {
                    append({
                        column: null,
                        value: null,
                    })

                    getColumns(
                        i,
                        getSelectedOption(
                            key,
                            tables.map((table) => ({
                                label: table,
                                value: table,
                            })),
                        ),
                    )
                        .then((options: any) => {
                            update(i, {
                                ...filters[i],
                                column: getSelectedOption(
                                    key,
                                    tables.map((table) => ({
                                        label: table,
                                        value: table,
                                    })),
                                ),
                                columns: options,
                                value: getSelectedOptions(tableFilter?.filters[key], options),
                            })
                        })
                        .catch((error) => {
                            console.error('Error :', error)
                        })
                    i++
                }
            } else {
                append({
                    column: null,
                    value: null,
                })
            }
        }
    }, [tableType])

    useEffect(() => {
        if (_for && experiment && tables.length > 0) {
            if (_for == 'RCT' || _for == 'Observational Study') {
                if (searchParams.get('for')) {
                    setTableType('Recommendation')
                    setTableFilter(experiment.experiment_metadatas?.recommendation_table_filters)
                } else {
                    setTableType('KPI')
                    setTableFilter(experiment.experiment_metadatas?.measurement_table_filters)
                }
            } else {
                setTableType('Intervention')
                setTableFilter(experiment.experiment_metadatas?.intervention_table_filters)
            }
        }
    }, [_for, experiment, tables])

    const mapOptions = Array.isArray(tables)
        ? tables?.map((table) => ({
              label: table,
              value: table,
          }))
        : []

    return (
        <div data-testid="add-filters-container">
            <div className="grid grid-cols-2 gap-4 mt-2">
                <Label text={t('Select Column')} className="!mb-0" data-testid="select-column-label" />
                <Label text={t('Value')} className="!mb-0" data-testid="value-label" />
            </div>
            {filters.map((filter: any, index) => {
                const selectedOptions = Array.isArray(filtersWatch) ? filtersWatch.map((item: any) => item.column) : []

                const availableOptions = mapOptions.filter((opt) => {
                    let used = false
                    for (const _selected of selectedOptions) {
                        if (filtersWatch[index].column?.value != opt?.value) {
                            if (_selected?.value == opt?.value) {
                                used = true
                            }
                        }
                    }
                    return !used
                })

                return (
                    <div key={filter.id} className="flex items-center gap-4 my-2">
                        <form onSubmit={handleSubmit(sendFilter)} id="add-data-filters" className="grid grid-cols-2 gap-4 flex-grow">
                            <div>
                                <Controller
                                    name={`filters.${index}.column`}
                                    control={control}
                                    rules={{ required: t('Please Select') }}
                                    render={({ field: { onChange, onBlur, value, ref, name } }) => (
                                        <Dropdown
                                            value={value}
                                            onChange={(data: any) => {
                                                trackEvent('measurement_filter_column_selected', { experiment_literal: literal, column_name: data?.label })
                                                // getColumns never rejects; its error callback only hides the loader.
                                                void getColumns(index, data)
                                                onChange(data)
                                            }}
                                            options={availableOptions}
                                            error={errors.filters?.[index]?.column?.message as string}
                                            data-testid={`dropdown-column-${index}`}
                                        />
                                    )}
                                />
                            </div>
                            <div className="flex items-center gap-4 ">
                                <Controller
                                    name={`filters.${index}.value`}
                                    control={control}
                                    rules={{ required: t('Please Select') }}
                                    render={({ field: { onChange, onBlur, value, ref, name } }) => (
                                        <Dropdown
                                            className="flex-grow "
                                            value={value}
                                            onChange={(data: any) => {
                                                trackEvent('measurement_filter_value_selected', { experiment_literal: literal, column_name: filtersWatch?.[index]?.column?.label, values_count: data?.length })
                                                onChange(data)
                                            }}
                                            isMulti={true}
                                            options={filter.columns || []}
                                            error={errors.filters?.[index]?.value?.message as string}
                                            data-testid={`dropdown-value-${index}`}
                                        />
                                    )}
                                />
                                {index != 0 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            remove(index)
                                        }}
                                        className="flex items-center justify-center cursor-pointer h-7 w-7 border border-[#c8c8c8] bg-[#f4f4f4] rounded-sm"
                                        data-testid={`remove-filter-${index}`}
                                    >
                                        &mdash;
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                )
            })}

            <div className="flex items-center justify-between my-4">
                <div>
                    {(filtersWatch || []).length < 3 && (
                        <Button
                            theme="secondary"
                            styleType="outline"
                            size="xs"
                            className="bg-[#f4f4f4] border-[#c8c8c8]"
                            onClick={() => {
                                trackEvent('measurement_filter_row_added', { experiment_literal: literal })
                                append({
                                    column: null,
                                    value: null,
                                })
                            }}
                            data-testid="add-row-button"
                        >
                            <Icon name="add-more" />
                            {t('Add Row')}
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        onClick={() => {
                            trackEvent('measurement_filter_cleared', { experiment_literal: literal })
                            remove()
                            append({
                                column: null,
                                value: null,
                            })
                            applyFilter(null)
                        }}
                        theme="secondary"
                        styleType="outline"
                        size="xs"
                        data-testid="clear-button"
                    >
                        {t('Clear')}
                    </Button>
                    <Button type="submit" form="add-data-filters" theme="secondary" size="xs" className="text-white" data-testid="apply-filter-button">
                        {t('Apply Filter')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default AddFilters
